import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/comics";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) {
      navigate(`/category/all?q=${encodeURIComponent(q)}`);
    }
  };

  const navLinkClass = (active: boolean) =>
    `whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors rounded-md ${
      active
        ? "text-primary-600"
        : isTransparent
          ? "text-background-50 hover:text-background-50/80"
          : "text-foreground-800 hover:text-primary-600"
    }`;

  const textPrimary = isTransparent ? "text-background-50" : "text-foreground-950";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-[72px] w-full items-center justify-between gap-3 px-4 md:px-6 lg:px-10">
        {/* Logo */}
        <h1 className="flex shrink-0 items-center gap-2">
          <Link to="/" className="flex items-center gap-2" aria-label="178漫画网 首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-fire-fill text-lg" aria-hidden="true" />
            </span>
            <span className={`font-heading text-lg md:text-xl font-bold ${textPrimary}`}>
              178<span className="text-primary-500">漫画</span>
              <span className={`ml-1 hidden text-xs font-normal lg:inline ${isTransparent ? "text-background-50/70" : "text-foreground-600"}`}>
                在线免费阅读
              </span>
            </span>
          </Link>
        </h1>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          <NavLink to="/" className={({ isActive }) => navLinkClass(isActive)} end>
            首页
          </NavLink>
          <NavLink to="/category/rexue" className={({ isActive }) => navLinkClass(isActive)}>
            漫画分类
          </NavLink>
          <NavLink to="/category/all?sort=hot" className={({ isActive }) => navLinkClass(isActive)}>
            人气榜单
          </NavLink>
          <NavLink to="/about" className={({ isActive }) => navLinkClass(isActive)}>
            关于我们
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {/* Search */}
          <form onSubmit={handleSearch} className="hidden items-center md:flex" role="search">
            <div
              className={`flex h-10 items-center gap-2 rounded-md border px-3 transition-colors ${
                isTransparent
                  ? "border-background-50/30 bg-background-50/10 text-background-50"
                  : "border-background-200 bg-background-100 text-foreground-800"
              }`}
            >
              <i className="ri-search-line text-sm" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画名"
                aria-label="搜索漫画"
                className={`w-28 bg-transparent text-sm outline-none transition-all focus:w-40 ${isTransparent ? "placeholder:text-background-50/60" : "placeholder:text-foreground-500"}`}
              />
            </div>
          </form>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`flex h-10 w-10 items-center justify-center rounded-md md:hidden ${
              isTransparent ? "text-background-50" : "text-foreground-950"
            }`}
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={handleSearch} className="mb-3" role="search">
            <div className="flex h-11 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 text-foreground-800">
              <i className="ri-search-line text-sm" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画名"
                aria-label="搜索漫画"
                className="w-full bg-transparent text-sm outline-none"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            <NavLink to="/" className={({ isActive }) => `rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "text-primary-600" : "text-foreground-800"}`} end>
              首页
            </NavLink>
            <NavLink to="/category/rexue" className={({ isActive }) => `rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "text-primary-600" : "text-foreground-800"}`}>
              漫画分类
            </NavLink>
            <NavLink to="/category/all?sort=hot" className={({ isActive }) => `rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "text-primary-600" : "text-foreground-800"}`}>
              人气榜单
            </NavLink>
            <NavLink to="/about" className={({ isActive }) => `rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "text-primary-600" : "text-foreground-800"}`}>
              关于我们
            </NavLink>
          </nav>
          <div className="mt-3 flex flex-wrap gap-2 border-t border-background-200 pt-3">
            {categories.slice(0, 6).map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}